import CampaignAIAPI from '@/api/campaigns/campaign-ai'
import { getStageFromStep } from '@/components/ai-assistant/ProgressStepper.vue'

/**
 * Campaign AI Vuex Store Module
 *
 * Manages state for the Campaign AI assistant feature.
 * Works in conjunction with the campaign-ai-websocket mixin.
 */

// Status constants
export const STATUS = {
  IDLE: 'idle',
  STARTING: 'starting',
  ACTIVE: 'active',
  AWAITING_ENTITIES: 'awaiting_entities',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
}

const initialState = () => ({
  // Session state
  sessionId: null,
  status: STATUS.IDLE,

  // Progress tracking
  currentStep: null,
  stepLabel: '',
  progressPercent: 0,
  progressStage: null,
  nextStep: null,
  nextStepLabel: '',

  // Messages
  messages: [],

  // Generated data from AI
  generatedCampaign: null,
  generatedBrief: null,
  generatedGroup: null,
  recommendedInfluencers: [],

  // Created entity IDs
  campaignId: null,
  briefId: null,
  groupId: null,

  // Session history
  sessions: [],
  sessionsLoading: false,

  // Error state
  error: null,

  // Loading states
  isStarting: false,
  isSending: false,
  isCreatingEntities: false,
})

export const state = initialState()

export const getters = {
  // Session state
  sessionId: (state) => state.sessionId,
  status: (state) => state.status,

  // Status checks
  isIdle: (state) => state.status === STATUS.IDLE,
  isActive: (state) => state.status === STATUS.ACTIVE,
  isAwaitingEntities: (state) => state.status === STATUS.AWAITING_ENTITIES,
  isProcessing: (state) => state.status === STATUS.PROCESSING,
  isCompleted: (state) => state.status === STATUS.COMPLETED,
  isFailed: (state) => state.status === STATUS.FAILED,
  isInProgress: (state) =>
    [STATUS.STARTING, STATUS.ACTIVE, STATUS.AWAITING_ENTITIES, STATUS.PROCESSING].includes(state.status),

  // Progress
  currentStep: (state) => state.currentStep,
  stepLabel: (state) => state.stepLabel,
  progressPercent: (state) => state.progressPercent,
  progressStage: (state) => state.progressStage,

  // Messages
  messages: (state) => state.messages,
  lastMessage: (state) => (state.messages.length > 0 ? state.messages[state.messages.length - 1] : null),

  // Generated data
  generatedCampaign: (state) => state.generatedCampaign,
  generatedBrief: (state) => state.generatedBrief,
  generatedGroup: (state) => state.generatedGroup,
  recommendedInfluencers: (state) => state.recommendedInfluencers,
  hasGeneratedEntities: (state) => !!(state.generatedCampaign && state.generatedBrief && state.generatedGroup),

  // Created entities
  campaignId: (state) => state.campaignId,
  briefId: (state) => state.briefId,
  groupId: (state) => state.groupId,
  hasCreatedEntities: (state) => !!(state.campaignId && state.briefId && state.groupId),

  // Sessions
  sessions: (state) => state.sessions,
  sessionsLoading: (state) => state.sessionsLoading,

  // Error
  error: (state) => state.error,
  hasError: (state) => !!state.error,

  // Loading
  isStarting: (state) => state.isStarting,
  isSending: (state) => state.isSending,
  isCreatingEntities: (state) => state.isCreatingEntities,
  isLoading: (state) => state.isStarting || state.isSending || state.isCreatingEntities,
}

export const mutations = {
  // Reset state
  RESET(state) {
    Object.assign(state, initialState())
  },

  // Reset session (keeps sessions list)
  RESET_SESSION(state) {
    const sessions = state.sessions
    Object.assign(state, initialState())
    state.sessions = sessions
  },

  // Session
  SET_SESSION_ID(state, sessionId) {
    state.sessionId = sessionId
  },

  SET_STATUS(state, status) {
    state.status = status
  },

  // Progress
  SET_PROGRESS(state, { step, step_label, percent, session_id, next_step, next_step_label }) {
    // Only update if for current session
    if (session_id && state.sessionId && session_id !== state.sessionId) return

    state.currentStep = step
    state.stepLabel = step_label
    state.progressPercent = percent
    state.progressStage = getStageFromStep(step)
    state.nextStep = next_step || null
    state.nextStepLabel = next_step_label || ''
  },

  ADVANCE_TO_NEXT_STEP(state) {
    if (state.nextStep) {
      state.currentStep = state.nextStep
      state.stepLabel = state.nextStepLabel || state.stepLabel
      state.progressStage = getStageFromStep(state.nextStep) || state.progressStage
      state.nextStep = null
      state.nextStepLabel = ''
    }
  },

  // Messages
  ADD_MESSAGE(state, message) {
    state.messages.push({
      ...message,
      timestamp: new Date().toISOString(),
    })
  },

  SET_MESSAGES(state, messages) {
    state.messages = messages
  },

  // Handle AI response from WebSocket
  HANDLE_RESPONSE(state, data) {
    // Only handle if for current session
    if (data.session_id && state.sessionId && data.session_id !== state.sessionId) return

    // Add AI message
    if (data.message) {
      state.messages.push({
        role: 'assistant',
        content: data.message,
        timestamp: new Date().toISOString(),
      })
    }

    // Update status based on action
    if (data.status) {
      state.status = data.status
    }

    // Handle specific actions
    if (data.action === 'create_entities' && data.data) {
      state.generatedCampaign = data.data.campaign || null
      state.generatedBrief = data.data.brief || null
      state.generatedGroup = data.data.group || null
      state.status = STATUS.AWAITING_ENTITIES
    }

    if (data.action === 'show_influencers' && data.data) {
      state.recommendedInfluencers = data.data.influencers || []
    }

    // Update progress stage from response's current_step
    const currentStep = data.current_step || data.result?.current_step
    if (currentStep) {
      state.currentStep = currentStep
      if (state.progressStage !== '__completed__') {
        state.progressStage = getStageFromStep(currentStep)
      }
    }
  },

  // Error handling
  SET_ERROR(state, { message, session_id }) {
    if (session_id && state.sessionId && session_id !== state.sessionId) return

    state.error = message
    state.status = STATUS.FAILED
  },

  CLEAR_ERROR(state) {
    state.error = null
  },

  // Completed
  SET_COMPLETED(state, { session_id, campaign_id }) {
    if (session_id && state.sessionId && session_id !== state.sessionId) return

    state.status = STATUS.COMPLETED
    state.progressStage = '__completed__'
    if (campaign_id) {
      state.campaignId = campaign_id
    }
  },

  // Created entities
  SET_CREATED_ENTITIES(state, { campaignId, briefId, groupId }) {
    state.campaignId = campaignId
    state.briefId = briefId
    state.groupId = groupId
  },

  // Sessions list
  SET_SESSIONS(state, sessions) {
    state.sessions = sessions
  },

  SET_SESSIONS_LOADING(state, loading) {
    state.sessionsLoading = loading
  },

  // Loading states
  SET_IS_STARTING(state, loading) {
    state.isStarting = loading
  },

  SET_IS_SENDING(state, loading) {
    state.isSending = loading
  },

  SET_IS_CREATING_ENTITIES(state, loading) {
    state.isCreatingEntities = loading
  },
}

export const actions = {
  /**
   * Start a new Campaign AI session.
   * After calling this, initialize WebSocket to receive events.
   */
  async startSession({ commit, state }) {
    if (state.isStarting) return

    commit('RESET_SESSION')
    commit('SET_IS_STARTING', true)
    commit('SET_STATUS', STATUS.STARTING)

    try {
      const { data } = await CampaignAIAPI.startSession()

      if (data.success) {
        commit('SET_SESSION_ID', data.session_id)
        commit('SET_STATUS', STATUS.ACTIVE)
        return data
      } else {
        commit('SET_ERROR', { message: data.error || 'Failed to start session' })
        return null
      }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to start session'
      commit('SET_ERROR', { message })
      return null
    } finally {
      commit('SET_IS_STARTING', false)
    }
  },

  /**
   * Send a message to the AI.
   */
  async sendMessage({ commit, state }, message) {
    if (!state.sessionId) {
      console.error('[CampaignAI] Cannot send message without session')
      return null
    }

    if (state.isSending) return

    commit('SET_IS_SENDING', true)
    commit('CLEAR_ERROR')

    // Add user message to state
    commit('ADD_MESSAGE', {
      role: 'user',
      content: message,
    })

    // Optimistically advance to next step
    if (state.nextStep) {
      commit('ADVANCE_TO_NEXT_STEP')
    }

    try {
      const { data } = await CampaignAIAPI.sendMessage(state.sessionId, message)

      if (data.success) {
        return data
      } else {
        commit('SET_ERROR', { message: data.error || 'Failed to send message' })
        return null
      }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to send message'
      commit('SET_ERROR', { message })
      return null
    } finally {
      commit('SET_IS_SENDING', false)
    }
  },

  /**
   * Select influencers from AI recommendations.
   */
  async selectInfluencers({ commit, state }, { selectedIds, selectAll = false }) {
    if (!state.sessionId) {
      console.error('[CampaignAI] Cannot select influencers without session')
      return null
    }

    commit('SET_IS_SENDING', true)
    commit('CLEAR_ERROR')

    // Optimistically advance to next step
    if (state.nextStep) {
      commit('ADVANCE_TO_NEXT_STEP')
    }

    try {
      const { data } = await CampaignAIAPI.selectInfluencers(
        state.sessionId,
        selectedIds,
        selectAll
      )

      if (data.success) {
        return data
      } else {
        commit('SET_ERROR', { message: data.error || 'Failed to select influencers' })
        return null
      }
    } catch (error) {
      const message = error.response?.data?.error || error.message || 'Failed to select influencers'
      commit('SET_ERROR', { message })
      return null
    } finally {
      commit('SET_IS_SENDING', false)
    }
  },

  /**
   * Load an existing session state.
   */
  async loadSession({ commit }, sessionId) {
    try {
      const { data } = await CampaignAIAPI.getSession(sessionId)

      if (data.success && data.session) {
        const session = data.session

        commit('SET_SESSION_ID', session.session_id)
        commit('SET_STATUS', session.status)
        commit('SET_PROGRESS', {
          step: session.current_step,
          step_label: session.step_label,
          percent: session.progress_percent,
        })
        commit('SET_MESSAGES', session.messages || [])

        if (session.campaign_id) {
          commit('SET_CREATED_ENTITIES', {
            campaignId: session.campaign_id,
            briefId: session.brief_id,
            groupId: session.group_id,
          })
        }

        return session
      }

      return null
    } catch (error) {
      console.error('[CampaignAI] Failed to load session:', error)
      return null
    }
  },

  /**
   * Load user's session history.
   */
  async loadSessions({ commit }, { limit = 10 } = {}) {
    commit('SET_SESSIONS_LOADING', true)

    try {
      const { data } = await CampaignAIAPI.getSessions({ limit })

      if (data.success) {
        commit('SET_SESSIONS', data.sessions)
        return data.sessions
      }

      return []
    } catch (error) {
      console.error('[CampaignAI] Failed to load sessions:', error)
      return []
    } finally {
      commit('SET_SESSIONS_LOADING', false)
    }
  },

  /**
   * Reset the current session.
   */
  resetSession({ commit }) {
    commit('RESET_SESSION')
  },

  /**
   * Clear error state.
   */
  clearError({ commit }) {
    commit('CLEAR_ERROR')
  },
}

// Export namespaced by default (required by the dynamic loader)
export const namespaced = true
